// 通知铃声 —— 用 Web Audio API 程序合成, 不依赖 mp3 文件
//
// 内置 3 个: "ding" / "chime" / "tap"
// + "none" 静音 + 自定义 (Settings 上传后存 dataURL 在 localStorage)

export type SoundId = "ding" | "chime" | "tap" | "ringer" | "none" | "custom";

export interface SoundMeta {
  id: SoundId;
  label: string;
  description: string;
}

export const BUILTIN_SOUNDS: SoundMeta[] = [
  { id: "ding", label: "Ding · 清亮短音", description: "默认 · 钢琴 G5 一拍" },
  { id: "chime", label: "Chime · 双音和弦", description: "苹果风 · 优雅" },
  { id: "tap", label: "Tap · 轻击", description: "低调 · 不打扰" },
  { id: "ringer", label: "Ringer · 电话铃", description: "更明显 · 不在前台时" },
  { id: "none", label: "静音", description: "只弹通知不响" },
];

let audioCtx: AudioContext | null = null;
function ctx(): AudioContext | null {
  if (audioCtx) return audioCtx;
  const AC = (window as unknown as { AudioContext?: typeof AudioContext })
    .AudioContext;
  if (!AC) return null;
  try {
    audioCtx = new AC();
    return audioCtx;
  } catch {
    return null;
  }
}

/** 播放指定铃声。"none" 静默, "custom" 走 localStorage 的 dataURL。 */
export async function playSound(id: SoundId): Promise<void> {
  if (id === "none") return;
  if (id === "custom") {
    const data = localStorage.getItem("chatbox.notify.custom-sound");
    if (!data) return;
    const audio = new Audio(data);
    audio.volume = 0.7;
    try {
      await audio.play();
    } catch {
      /* autoplay blocked */
    }
    return;
  }
  const c = ctx();
  if (!c) return;
  if (c.state === "suspended") {
    try {
      await c.resume();
    } catch {
      return;
    }
  }
  switch (id) {
    case "ding":
      synthDing(c);
      break;
    case "chime":
      synthChime(c);
      break;
    case "tap":
      synthTap(c);
      break;
    case "ringer":
      synthRinger(c);
      break;
  }
}

/** 单音 envelop helper */
function tone(
  c: AudioContext,
  freq: number,
  start: number,
  duration: number,
  peakGain = 0.25,
  type: OscillatorType = "sine",
) {
  const osc = c.createOscillator();
  osc.type = type;
  osc.frequency.value = freq;
  const g = c.createGain();
  g.gain.setValueAtTime(0, c.currentTime + start);
  g.gain.linearRampToValueAtTime(peakGain, c.currentTime + start + 0.01);
  g.gain.exponentialRampToValueAtTime(0.0001, c.currentTime + start + duration);
  osc.connect(g);
  g.connect(c.destination);
  osc.start(c.currentTime + start);
  osc.stop(c.currentTime + start + duration + 0.05);
}

function synthDing(c: AudioContext) {
  tone(c, 880, 0, 0.45, 0.3); // A5
  tone(c, 1320, 0.02, 0.35, 0.18); // E6 泛音
}

function synthChime(c: AudioContext) {
  tone(c, 660, 0, 0.5, 0.25); // E5
  tone(c, 990, 0.08, 0.6, 0.22); // B5
}

function synthTap(c: AudioContext) {
  tone(c, 380, 0, 0.12, 0.4, "triangle");
}

function synthRinger(c: AudioContext) {
  // 两短一长, 类似手机来电
  tone(c, 800, 0, 0.15, 0.32);
  tone(c, 800, 0.2, 0.15, 0.32);
  tone(c, 1000, 0.45, 0.5, 0.35);
}

// ---------- 持久化 ----------

const PREF_KEY = "chatbox.notify.sound";
const CUSTOM_KEY = "chatbox.notify.custom-sound";

export function getSoundPref(): SoundId {
  try {
    const v = localStorage.getItem(PREF_KEY);
    if (
      v === "ding" ||
      v === "chime" ||
      v === "tap" ||
      v === "ringer" ||
      v === "none" ||
      v === "custom"
    )
      return v;
  } catch {
    /* ignore */
  }
  return "ding";
}

export function setSoundPref(id: SoundId): void {
  try {
    localStorage.setItem(PREF_KEY, id);
  } catch {
    /* ignore */
  }
}

export function setCustomSoundDataUrl(dataUrl: string): void {
  try {
    localStorage.setItem(CUSTOM_KEY, dataUrl);
  } catch {
    /* ignore */
  }
}

export function hasCustomSound(): boolean {
  try {
    return !!localStorage.getItem(CUSTOM_KEY);
  } catch {
    return false;
  }
}

export function clearCustomSound(): void {
  try {
    localStorage.removeItem(CUSTOM_KEY);
  } catch {
    /* ignore */
  }
}
